import React, { useEffect, useState } from "react";
import { TopNav } from "@/components/TopNav";
import { api } from "@/lib/api";
import { RankBadge } from "@/components/RankBadge";
import { Trophy, Flame, Crown, Medal } from "lucide-react";

const TABS = [
  { key: "all", label: "Tổng (ELO)" },
  { key: "today", label: "Hôm nay" },
  { key: "week", label: "Tuần" },
  { key: "month", label: "Tháng" },
  { key: "streak", label: "Chuỗi thắng" },
  { key: "wins", label: "Số trận thắng" },
];

export default function Leaderboard() {
  const [period, setPeriod] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboard?period=${period}&limit=50`).then(({ data }) => {
      setRows(data);
      setLoading(false);
    });
  }, [period]);

  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Trophy className="text-yellow-400" />
          <h1 className="font-display font-bold text-3xl sm:text-4xl">Bảng xếp hạng</h1>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setPeriod(t.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                period === t.key
                  ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/40 glow-cyan"
                  : "bg-white/[0.03] text-white/60 border border-white/8 hover:bg-white/5"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-white/40 py-20 text-center">Đang tải...</div>
        ) : (
          <>
            {/* Podium */}
            {podium.length > 0 && (
              <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-8">
                {[1, 0, 2].map((idx) => {
                  const p = podium[idx];
                  if (!p) return <div key={idx} />;
                  const ranks = [["text-yellow-400", "from-yellow-500/30 to-yellow-700/5", "border-yellow-400/50", <Crown size={20} key="c" />, "#facc15"],
                                 ["text-slate-300", "from-slate-300/20 to-slate-500/5", "border-slate-300/40", <Medal size={20} key="m" />, "#cbd5e1"],
                                 ["text-orange-400", "from-orange-500/20 to-orange-700/5", "border-orange-400/40", <Medal size={20} key="m" />, "#fb923c"]];
                  const [tc, bg, bd, icon, color] = ranks[idx];
                  const h = idx === 0 ? "h-44 sm:h-56" : idx === 1 ? "h-36 sm:h-44" : "h-32 sm:h-40";
                  return (
                    <div key={p.id} className={`relative rounded-xl bg-gradient-to-b ${bg} border ${bd} p-4 sm:p-5 flex flex-col items-center justify-end ${h}`}>
                      <div className={`absolute -top-6 sm:-top-7 left-1/2 -translate-x-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 overflow-hidden bg-[#0c0d14]`} style={{ borderColor: color }}>
                        <img src={p.avatar_url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className={`absolute top-2 right-2 ${tc}`}>{icon}</div>
                      <div className={`font-display font-bold text-sm sm:text-base mt-6 text-center truncate w-full`}>{p.nickname}</div>
                      <div className={`font-display font-extrabold text-2xl sm:text-3xl ${tc} text-shadow-neon`} style={{ color }}>{p.elo}</div>
                      <div className="text-[10px] text-white/50 uppercase tracking-wider mt-1">{p.rank}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* List */}
            <div className="rounded-xl bg-[#0c0d14] border border-white/8 overflow-hidden">
              <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/8">
                <div className="col-span-1">#</div>
                <div className="col-span-5 sm:col-span-4">Người chơi</div>
                <div className="col-span-3 sm:col-span-2">Rank</div>
                <div className="col-span-3 sm:col-span-2 text-right">ELO</div>
                <div className="hidden sm:block sm:col-span-2 text-right">Win Rate</div>
                <div className="hidden sm:block sm:col-span-1 text-right">Streak</div>
              </div>
              {rest.map((p, i) => (
                <div key={p.id} className="grid grid-cols-12 items-center px-5 py-3 border-b border-white/5 hover:bg-white/[0.03] transition" data-testid="lb-row">
                  <div className="col-span-1 font-mono-num text-sm text-white/60">{i + 4}</div>
                  <div className="col-span-5 sm:col-span-4 flex items-center gap-3 min-w-0">
                    <img src={p.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                    <span className="truncate">{p.nickname}</span>
                  </div>
                  <div className="col-span-3 sm:col-span-2"><RankBadge elo={p.elo} size="sm" showElo={false} /></div>
                  <div className="col-span-3 sm:col-span-2 text-right font-display font-bold">{p.elo}</div>
                  <div className="hidden sm:block sm:col-span-2 text-right text-white/70">{p.win_rate}%</div>
                  <div className="hidden sm:flex sm:col-span-1 items-center justify-end gap-1 text-red-400">
                    <Flame size={12} /> {p.win_streak}
                  </div>
                </div>
              ))}
              {rows.length === 0 && <div className="py-20 text-center text-white/40">Chưa có dữ liệu</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
